import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaTelegram, FaLock, FaExchangeAlt, FaClock, FaUserShield, FaCheckCircle, FaCoins, FaRobot, FaExclamationCircle, FaInfoCircle, FaTimes, FaShieldAlt } from 'react-icons/fa';
import HolyLogo from '../components/HolyLogo';

const PageContainer = styled.div`
  min-height: calc(100vh - 4rem);
  background-color: ${props => props.theme.colors.darkBg};
  color: ${props => props.theme.colors.text};
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
  position: relative;
`;

const ContentWrapper = styled.div`
  ${props => props.$isModalOpen && `
    filter: blur(2px);
    pointer-events: none;
    user-select: none;
  `}
  transition: filter 0.2s ease;
`;

const Hero = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  background: ${props => props.theme.colors.darkBg2};
  border-radius: 15px;
  margin-bottom: 2rem;
  animation: fadeIn 1s ease-in;
  border: 1px solid rgba(255, 255, 255, 0.05);

  h1 {
    font-size: clamp(2rem, 5vw, 3.5rem);
    margin-bottom: 1rem;
    background: ${props => props.theme.colors.gradient1};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 700;
  }

  p {
    font-size: clamp(1rem, 3vw, 1.5rem);
    color: ${props => props.theme.colors.text};
    opacity: 0.9;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const FeatureCard = styled.div`
  background: ${props => props.theme.colors.darkBg2};
  padding: 1.5rem;
  border-radius: 15px;
  text-align: center;
  transition: transform 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);

  @media (prefers-reduced-motion: reduce) {
    transform: none !important;
    transition: none !important;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: ${props => props.theme.colors.primary};
  }

  h3 {
    font-size: 1.25rem;
    margin: 1rem 0;
    color: ${props => props.theme.colors.text};
  }

  p {
    font-size: 1rem;
    line-height: 1.6;
    color: ${props => props.theme.colors.textMuted};
  }

  svg {
    font-size: 2rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 0.5rem;
  }
`;

const HowItWorks = styled.div`
  text-align: center;
  margin: 3rem 0;

  h2 {
    font-size: clamp(1.5rem, 4vw, 2rem);
    margin-bottom: 2rem;
  }
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const Step = styled.div`
  position: relative;
  padding: 1.5rem;
  background: ${props => props.theme.colors.darkBg2};
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.05);

  .step-number {
    width: 36px;
    height: 36px;
    background: ${props => props.theme.colors.gradient1};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    font-weight: bold;
    color: ${props => props.theme.colors.white};
  }

  p {
    font-size: 1rem;
    color: ${props => props.theme.colors.text};
    margin: 0.5rem 0 0;

    &.step-description {
      color: ${props => props.theme.colors.textMuted};
      font-size: 0.9rem;
      margin-top: 0.5rem;
    }
  }
`;

const CTASection = styled.div`
  text-align: center;
  margin: 3rem 0;
  padding: 2rem;
  background: ${props => props.theme.colors.darkBg2};
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.05);

  h2 {
    font-size: clamp(1.5rem, 4vw, 2rem);
    margin-bottom: 1.5rem;
  }

  p {
    margin-bottom: 2rem;
    color: ${props => props.theme.colors.textMuted};
  }
`;

const TelegramButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  padding: 0.875rem 1.75rem;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (prefers-reduced-motion: reduce) {
    transform: none !important;
    transition: none !important;
  }

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.theme.colors.primaryDark};
    box-shadow: 0 5px 15px rgba(108, 92, 231, 0.3);
    color: ${props => props.theme.colors.white};
  }

  svg {
    font-size: 1.25rem;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.colors.darkBg2};
  border: 1px solid ${props => props.theme.colors.primary}33;
  border-radius: 12px;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  animation: ${fadeIn} 0.3s ease-out;
  margin: 1rem;
  pointer-events: auto;

  p {
    color: ${props => props.theme.colors.text};
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
`;

const ModalButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border: none;
  border-radius: 8px;
  padding: 0.75rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    background: ${props => props.theme.colors.primaryLight};
    transform: translateY(-2px);
  }
`;

const InfoIcon = styled(FaInfoCircle)`
  color: ${props => props.theme.colors.primary};
  margin-left: 0.5rem;
  cursor: pointer;
  font-size: 1rem;
  vertical-align: middle;
  position: relative;
  top: -2px;
  transition: transform 0.2s ease, color 0.2s ease;

  &:hover {
    transform: scale(1.1);
    color: ${props => props.theme.colors.primaryLight};
  }
`;

const Highlight = styled.a`
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: ${props => props.theme.colors.primaryLight};
  }
`;

const Command = styled.code`
  background: ${props => props.theme.colors.darkBg3};
  color: ${props => props.theme.colors.primary};
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.95em;
  border: 1px solid ${props => props.theme.colors.primary}33;
`;

const EscrowPage = () => {
  const [showModal, setShowModal] = useState(false);

  React.useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  return (
    <>
      <PageContainer>
        <ContentWrapper $isModalOpen={showModal}>
          <Hero>
            <h1>HolyHubs Escrow</h1>
            <p>La solution sécurisée pour vos transactions en ligne</p>
          </Hero>

          <FeaturesGrid>
            <FeatureCard>
              <FaUserShield />
              <h3>Sécurité Garantie</h3>
              <p>Protection complète de vos transactions avec notre système d'escrow vérifié et approuvé par la communauté.</p>
            </FeatureCard>

            <FeatureCard>
              <FaCoins />
              <h3>Frais Transparents</h3>
              <p>Des frais de service clairs et compétitifs. Seulement 10% du montant de la transaction.</p>
            </FeatureCard>

            <FeatureCard>
              <FaClock />
              <h3>Service 24/7</h3>
              <p>Notre équipe est disponible en permanence pour assurer la sécurité de vos transactions.</p>
            </FeatureCard>
          </FeaturesGrid>

          <HowItWorks>
            <h2>Comment ça marche ?</h2>
            <StepsGrid>
              <Step>
                <div className="step-number">1</div>
                <p>Contactez notre bot Telegram</p>
                <p className="step-description">
                  Démarrez une conversation avec <Highlight href="https://t.me/hhescrowbot" target="_blank" rel="noopener noreferrer">@hhescrowbot</Highlight> sur Telegram
                  <InfoIcon onClick={() => setShowModal(true)} />
                </p>
              </Step>
              <Step>
                <div className="step-number">2</div>
                <p>Décrivez votre transaction</p>
                <p className="step-description">Spécifiez le montant, la méthode de paiement et les détails de votre échange</p>
              </Step>
              <Step>
                <div className="step-number">3</div>
                <p>Validation des conditions</p>
                <p className="step-description">Les deux parties confirment les termes et acceptent les frais de service</p>
              </Step>
              <Step>
                <div className="step-number">4</div>
                <p>Transfert sécurisé</p>
                <p className="step-description">L'argent est sécurisé jusqu'à la confirmation de réception</p>
              </Step>
            </StepsGrid>
          </HowItWorks>

          <FeaturesGrid>
            <FeatureCard>
              <FaShieldAlt />
              <h3>Protection Acheteur</h3>
              <p>Votre argent est sécurisé jusqu'à la réception et la validation de votre achat. Remboursement garanti en cas de problème.</p>
            </FeatureCard>

            <FeatureCard>
              <FaLock />
              <h3>Protection Vendeur</h3>
              <p>Garantie de paiement une fois la livraison confirmée. Protection contre les rétrofacturations frauduleuses.</p>
            </FeatureCard>

            <FeatureCard>
              <FaCheckCircle />
              <h3>Résolution des Litiges</h3>
              <p>Service de médiation professionnel disponible 24/7 en cas de désaccord entre les parties.</p>
            </FeatureCard>
          </FeaturesGrid>

          <CTASection>
            <h2>Prêt à sécuriser votre transaction ?</h2>
            <p>Notre service d'escrow est disponible 24/7 pour garantir la sécurité de vos échanges.</p>
            <TelegramButton 
              href="https://t.me/hhescrowbot" 
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaTelegram /> Démarrer avec notre Bot Telegram
            </TelegramButton>
          </CTASection>
        </ContentWrapper>
      </PageContainer>
      {showModal && <InfoModal isOpen={showModal} onClose={() => setShowModal(false)} />}
    </>
  );
};

const InfoModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  // Empêcher la propagation des clics
  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  return (
    <ModalOverlay>
      <ModalContent onClick={handleContentClick}>
        <p>
          Les deux parties de l'escrow doivent impérativement s'enregistrer avec la commande <Command>/start</Command> avant de pouvoir effectuer une transaction. Cette étape est obligatoire pour garantir la sécurité et la traçabilité de vos échanges.
        </p>
        <ModalButton onClick={onClose}>
          Compris !
        </ModalButton>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EscrowPage; 